import React, { useState } from 'react';
import DayCard from './DayCard';
import AffiliateDisclosure from '../AffiliateDisclosure';
import { getCurrentDayNumber, getDayWeather } from './dashboardUtils';

/**
 * DayCardsGrid - Full-width stack of collapsible day cards. The current day
 * (or day 1 if the trip hasn't started) is expanded by default; all others
 * are collapsed.
 */
const PAID_TIERS = ['individual', 'group', 'starter_agent', 'professional_agent', 'enterprise_agency', 'promotional', 'unlimited', 'admin'];

const DayCardsGrid = ({
  days,
  startDate,
  weatherData,
  attractionStatuses,
  onViewDay,
  onSwapDays,
  isSwapping,
  isOwner,
  userTier,
  limitsWaived = false,
  onViewPricing,
  destination,
  onRemoveStop,
  bookingSurface,
  // Trip-level context for the Day-1 "getting around" mounts: the transfer is
  // dated to arrival, and car hire only makes sense once we know the traveller
  // is driving.
  endDate,
  transportationMode,
}) => {
  // limitsWaived: non-paid user on a short trip (<= 3 days) gets full access.
  const isFreeUser = !limitsWaived && !PAID_TIERS.includes(userTier || 'free');
  const currentDayNumber = getCurrentDayNumber(startDate);

  // Pick initial expanded day: current day if the trip is in progress and
  // that day exists in this itinerary; otherwise the first day. Multiple
  // days can be expanded at once — each card toggles independently.
  const dayNumbers = (days || []).map((d) => d.day);
  const initialExpandedDay =
    currentDayNumber !== null && dayNumbers.includes(currentDayNumber)
      ? currentDayNumber
      : (dayNumbers[0] ?? null);
  const [expandedDays, setExpandedDays] = useState(
    () => new Set(initialExpandedDay !== null ? [initialExpandedDay] : []),
  );

  if (!days || days.length === 0) {
    return (
      <div className="text-center py-12 bg-neutral-50 rounded-xl border border-neutral-200">
        <p className="text-neutral-500 text-lg">No days found in this itinerary.</p>
        <p className="text-neutral-400 text-sm mt-2">
          Days are extracted from your itinerary content.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {days.map((day, index) => {
        const weather = getDayWeather(day.day, weatherData);
        const isCurrentDay = currentDayNumber !== null && day.day === currentDayNumber;

        const isDayLocked = isFreeUser && day.day > 1;

        return (
          <DayCard
            key={day.day}
            day={day}
            startDate={startDate}
            weather={weather}
            attractionStatuses={attractionStatuses}
            isCurrentDay={isCurrentDay}
            onViewDay={isDayLocked ? () => onViewPricing && onViewPricing() : () => onViewDay(day)}
            isFirst={index === 0}
            isLast={index === days.length - 1}
            onSwapDays={!isDayLocked && isOwner && onSwapDays ? onSwapDays : undefined}
            allDays={days}
            isSwapping={isSwapping}
            isLocked={isDayLocked}
            destination={destination}
            bookingSurface={bookingSurface}
            endDate={endDate}
            transportationMode={transportationMode}
            onRemoveStop={!isDayLocked && isOwner && onRemoveStop ? onRemoveStop : undefined}
            isOwner={isOwner}
            isExpanded={expandedDays.has(day.day)}
            onToggleExpand={() =>
              setExpandedDays((cur) => {
                const next = new Set(cur);
                if (next.has(day.day)) {
                  next.delete(day.day);
                } else {
                  next.add(day.day);
                }
                return next;
              })
            }
          />
        );
      })}
      {/* The day cards carry hotel, ticket, transfer and car links, so the page
          they render on needs the disclosure. */}
      <AffiliateDisclosure className="px-1" />
    </div>
  );
};

export default DayCardsGrid;
